import React, { ReactElement, useContext, useEffect } from "react";
import { AppContext, BookingData } from "../../AppDataProvider/AppDataProvider";
import { StepTitle as BaseStepTitle, Button } from "../../common/components";
import { corners, gap, margin, marginBottom, media, typography } from "../../common/styles";
import { useApolloClient } from "@apollo/client";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ActionType } from "../../AppDataProvider/actions";
import { BookingVisibilityType } from "../../GeneratedGraphQL";
import { Mixpanel } from "../../mixpanel";
import { requireBookingDataFields } from "../../utils";
import { PartRequired } from "../../utils/types";
import Summary from "./Summary/Summary";
type BookingCreatedBookingData = PartRequired<BookingData, "organisation" | "service" | "userDetails">;
export function BookingCreatedStep(): ReactElement {
  const {
    t
  } = useTranslation();
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  const client = useApolloClient();
  useEffect(() => {
    Mixpanel.track({
      name: "Booking created"
    });
  }, []);
  requireBookingDataFields<BookingCreatedBookingData>(appData.bookingData, ["organisation", "service", "userDetails"]);
  const {
    organisation,
    service,
    totalPaid,
    userDetails
  } = appData.bookingData;
  const {
    areConfirmationEmailsEnabled,
    areInvoiceEmailsEnabled,
    bookingConfirmationMessage
  } = organisation;
  const isRequestBooking = service.bookingVisibilityType === BookingVisibilityType.Request;
  const showEmailLine = areConfirmationEmailsEnabled || areInvoiceEmailsEnabled && totalPaid !== undefined;
  const makeAnotherBooking = () => {
    void client.clearStore().then(() => {
      appDataDispatch({
        type: ActionType.RESTART_BOOKING_FLOW
      });
    });
  };
  return <Container data-sentry-element="Container" data-sentry-component="BookingCreatedStep" data-sentry-source-file="BookingCreatedStep.tsx">
      <Title data-sentry-element="Title" data-sentry-source-file="BookingCreatedStep.tsx">
        <CheckIcon data-sentry-element="CheckIcon" data-sentry-source-file="BookingCreatedStep.tsx" />
        <StepTitle data-sentry-element="StepTitle" data-sentry-source-file="BookingCreatedStep.tsx">
          {t(isRequestBooking ? "stepTitle.bookingRequested" : "stepTitle.bookingCreated")}
        </StepTitle>
      </Title>
      {service.image && <ServiceImage src={service.image} alt={service.name} />}
      {bookingConfirmationMessage && <Text dangerouslySetInnerHTML={{
      __html: bookingConfirmationMessage
    }} />}
      <Summary data-sentry-element="Summary" data-sentry-source-file="BookingCreatedStep.tsx" />
      {showEmailLine && <Text className="sentry-mask">
          {t("confirmationEmailSent", {
        email: userDetails.email
      })}
        </Text>}
      <Button variant="secondary" onClick={makeAnotherBooking} data-sentry-element="Button" data-sentry-source-file="BookingCreatedStep.tsx">
        {t("makeAnotherBooking")}
      </Button>
    </Container>;
}
export default BookingCreatedStep;
const Container = styled.div`
  width: 100%;
  max-width: 22.5rem;
  align-self: center;

  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;
const Title = styled.div`
  display: flex;

  flex-direction: row;

  ${gap.m}

  @media ${media.mobile} {
    flex-direction: column;
  }

  align-items: center;
  justify-content: center;
  ${marginBottom.m}
`;
const CheckIcon = styled(FontAwesomeIcon).attrs({
  icon: faCheckCircle,
  size: "2x"
})`
  color: ${({
  theme
}) => theme.colours.success};
`;
const StepTitle = styled(BaseStepTitle)`
  ${margin.zero}
`;
const ServiceImage = styled.img`
  width: 100%;
  object-fit: cover;
  ${corners.m}
  ${marginBottom.m}
`;
const Text = styled.div`
  ${typography.body}
  ${marginBottom.m}
`;